/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, StandardLoadOptions, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { task } from 'app/shared/lib/decorators';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { MemberService, IMemberSearchFilters, countMemberFilters } from "app/cd/services/member-service";
import { IPopup } from 'app/shared/elements/popup-base';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { MemberDisplay } from 'app/cd/elements/members/member-display';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { guid } from 'app/shared/lib/guid';
import { SetFocusEvent } from 'app/shared/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class MemberSelect extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    protected member: CD.Member | null = null;

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    private searchFilters: IMemberSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    private tempSearchFilters: IMemberSearchFilters = {};

    @bindable.boolean
    private includeContactData: boolean = false;

    @bindable.boolean()
    public includeHouseholdData: boolean = false;

    @bindable.boolean()
    protected showHouseholdFields: boolean = false;

    @bindable.boolean
    protected autoSelectOnlyResult: boolean = true;

    @bindable.boolean()
    public required: boolean = false;

    @bindable.objectNull()
    public availableFilters: string[] | null = [
        'active', 'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate', 'primaryCarePhysician',
        'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last', 'healthPlan', 'lineOfBusiness'
    ];

    private generalSearchTerm: string | null = null;
    private memberResults: ReadonlyArray<CD.Member> | null = null;
    private useFilters: boolean = true;
    private useSpecificFilters: boolean = false;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected hasIneligibleMembers: boolean = false;

    ////****************** Element References ***************************************////
    protected $memberDisplay!: MemberDisplay;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $collapsibleSearchCriteria!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;

    protected id: guid = guid();


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        protected transitional: TransitionalManager,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(this.showHouseholdFields && this.availableFilters) {
            this.availableFilters.push('household.name', 'household.id')
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only trigger search if there is data to sort
        if(this.memberResults?.length) await this.search(this.useFilters);
    }


    ////****************** Functions ************************************************////
    public async generalSearch(): Promise<void> {
        this.searchFilters = {};
        if(this.generalSearchTerm) {
            this.searchFilters.generalSearchTerm = this.generalSearchTerm;
        }
        this.search(true, true);
    }

    public async search(useFilters: boolean = true, openModal: boolean = false): Promise<void> {
        this.useFilters = useFilters;
        this.useSpecificFilters = this.$modal.state == OpenFlags.Open;

        await this.searchMembers();

        if(this.memberResults?.length === 1 && this.autoSelectOnlyResult && !this.hasHouseholdMembership(this.memberResults[0])) {
            this.selectMember(this.memberResults[0]);
        } else if(openModal) {
            this.$modal.open();
        }
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    protected handleSearchButton(): void {
        if(this.$pager) this.$pager.updatePage(0);
        this.searchFilters = { ...this.tempSearchFilters };
        this.search();
    }

    public selectMember(member: CD.Member) {
        this.searchFilters = {};
        this.member = member;
        if(this.$memberDisplay) this.$memberDisplay.setMember(this.member);
        this.$modal.close();
        this.clear();
    }

    protected closeModal(): void {
        this.clear();
        this.$modal.close();
    }

    public clear(returnFocus?: boolean): void {
        this.memberResults = new Array<CD.Member>();
        this.searchFilters = {}
        this.tempSearchFilters = {}
        this.generalSearchTerm = null;
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.useSpecificFilters = false;
        this.$collapsible.open(0);

        if(returnFocus) this.events.publish(new SetFocusEvent(this.$collapsibleSearchCriteria));
    }

    @task
    protected memberCleared(): void {
        this.member = null;
        this.searchFilters.generalSearchTerm = this.generalSearchTerm = null;
        if(this.$memberDisplay) {
            this.$memberDisplay.clear();
        }
    }

    protected memberChanged(member: CD.Member): void {
        if(this.$memberDisplay && member) { this.$memberDisplay.setMember(member); }
    }

    protected isValidSelection(member: CD.Member): boolean {
        // if household data included, check if member already has a household membership
        if(this.includeHouseholdData) {
            return !this.hasHouseholdMembership(member);
        }
        return true;
    }

    protected hasHouseholdMembership(member: CD.Member): boolean {
        if(!this.includeHouseholdData || !member.household?.memberships) return false;
        const hasActiveMembership = member.household.memberships.some(membership => membership.isActive);
        return hasActiveMembership;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        return true;
    }
    public reset?(): void {
        this.clear();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchMembers(): Promise<void> {
        if(countMemberFilters(this.searchFilters) === 0) {
            this.$collapsible.open(0);
            this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.minimumFilterRequirements')).show();
            this.memberResults = [];
            return;
        } else {
            this.$collapsible.open(1);
        }

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            const includes = [
                "primaryCarePhysician",
                "primaryPlan",
                "primaryPlan.healthPlan",
                "primaryPlan.lineOfBusiness"
            ]

            const fields = [
                'birthDate',
                'eligibility.isCurrent',
                'primaryPlan.healthPlan.description',
                'primaryPlan.lineOfBusiness.description',
                'primaryCarePhysician.identifier.primary',
                'primaryCarePhysician.name.full',
            ];

            if(this.includeContactData) {
                includes.push(
                    'address.mailing',
                    'phoneNumber.cell',
                    'phoneNumber.fax',
                    'phoneNumber.home',
                    'phoneNumber.work',
                );
                fields.push(
                    'address.mailing.city',
                    'address.mailing.postalCode',
                    'address.mailing.region.*',
                    'address.mailing.street1',
                    'address.mailing.street2',
                    'emailAddress',
                    'phoneNumber.cell.number',
                    'phoneNumber.fax.number',
                    'phoneNumber.home.number',
                    'phoneNumber.work.number'
                );
            }

            if(this.includeHouseholdData) {
                includes.push('household.memberships');
                fields.push('household.memberships.isActive');
            }

            memberRequiredFields.forEach(field => !fields.includes(field) ? fields.push(field) : null);

            const response = await this.memberService.search(this.searchFilters, options, includes, fields);
            this.memberResults = response.results;
            this.resultCount = response.count || 0;

            // If results include some ineligible members, show the warning column
            this.hasIneligibleMembers = this.memberResults.some(m => { return m.eligibility && !m.eligibility.isCurrent; });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
